import axios from "axios";


export interface DateRangeDTO {
    id: number;
    startDate: string;
    endDate: string;
    pricingModelId?: number;
    }


export interface PackageImageUrlDTO {
    id: number;
    url: string;
    isMain: boolean;
}

export enum ChaletType {
  Big = 0,
  Small = 1,
}

export interface PricingModelDTO {
    id: number;
    packageId: number;
    description: string;
    price: number;
    discount: number;
    minPersons: number;
    maxPersons: number;
    dateRanges: DateRangeDTO[];
    bookedPersons?: number;
    chaletCapacity?: number;
    packageDTO?: PackageDTO;
    chaletType: ChaletType ; // Change type to ChaletType
}



export interface PackageDTO {
    id?: number;
    name: string;
    description: string;
    categoryId: number;
    categoryName?: string;
    priceFrom: number;
    maxGroupSize: number;
    packageUrl?: string;
    prices: PricingModelDTO[];
    imageUrls: PackageImageUrlDTO[];
    mainImageId?: number;
}

export interface CategoryDTO {
    id: number;
    name: string;
    // title: string;
    parentCategoryId: number;
    // description: string;
    packages: PackageDTO[];
}

export interface ServiceResponse<T> {
    success: boolean;
    message: string | null;
    data: T;
}

export const getCategories = async (apiUrl: string): Promise<ServiceResponse<CategoryDTO[]>> => {
    try {
        const response = await axios.get<ServiceResponse<CategoryDTO[]>>(`${apiUrl}/Category/getCategories`);
        return response.data;
    } catch (error: any) {
        return { success: false, message: error.message, data: [] };
    }
}

export const fetchCategories = async (apiUrl: string): Promise<CategoryDTO[]> => {
    try {
        const response = await axios.get<{ success: boolean; message: string | null; data: CategoryDTO[] }>(`${apiUrl}/Category/getCategories`, {

        });
        if (response.data.success) {
            console.log('Categories:', response.data.data);
            return response.data.data;
        } else {
            console.error('Error fetching categories:', response.data.message);
            return [];
        }
    } catch (error) {
        console.error('Error fetching categories:', error);
        return [];
    }
};

export const addPackage = async (packageDto: PackageDTO, images: File[], mainImageIndex?: number, apiUrl?: string): Promise<ServiceResponse<PackageDTO>> => {
    const formData = new FormData();
    formData.append('packageDto', JSON.stringify(packageDto));
    
    images.forEach((image) => {
        formData.append('newImages', image); // Use 'newImages' as the key
    });
    
    if (mainImageIndex !== undefined) {
        formData.append('mainImageIndex', mainImageIndex.toString());
    }
    
    try {
        const response = await axios.post<ServiceResponse<PackageDTO>>(`${apiUrl}/Package/addPackage`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error: any) {
        return { success: false, message: error.message, data: {} as PackageDTO };
    }
};


export const updatePackage = async (packageDto: PackageDTO, images: File[], mainImageIndex?: number, apiUrl?: string, mainImageId?: number): Promise<ServiceResponse<PackageDTO>> => {
    const formData = new FormData();
    formData.append('packageDto', JSON.stringify({ ...packageDto, prices: undefined })); // Exclude prices from packageDto
    // Prices/dates are saved only through the pricing form. Re-sending a (possibly stale) copy here would
    // make the backend delete any pricing models and date ranges missing from it.
    if (packageDto.prices && packageDto.prices.length > 0) {
        formData.append('prices', JSON.stringify(packageDto.prices));
    }

    images.forEach((image) => {
        formData.append('newImages', image); // Use 'newImages' as the key
    });
    if (mainImageIndex !== undefined) {
        formData.append('mainImageIndex', mainImageIndex.toString());
    }
    if (mainImageId !== undefined) {
        formData.append('mainImageId', mainImageId.toString());
    }
    try {
        console.log("Updating Package:", packageDto); // Log the data being sent
        const response = await axios.put<ServiceResponse<PackageDTO>>(`${apiUrl}/Package/updatePackage/${packageDto.id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error: any) {
        return { success: false, message: error.message, data: {} as PackageDTO };
    }
};

export const fetchPackageById = async (packageId: number, apiUrl?: string): Promise<ServiceResponse<PackageDTO | null>> => {
    try {
        const response = await axios.get<ServiceResponse<PackageDTO>>(`${apiUrl}/Package/getPackageById/${packageId}`, {
            headers: { Authorization: "Bearer " + (localStorage.getItem("accessToken") ?? "") },
        });
        return response.data;
    } catch (error: any) {
        return { success: false, message: error.message, data: null };
    }
};

export const deletePackage = async (packageId: number, apiUrl?: string): Promise<ServiceResponse<boolean>> => {
    try {
        const response = await axios.delete<ServiceResponse<boolean>>(`${apiUrl}/Package/deletePackage/${packageId}`);
        return response.data;
    } catch (error: any) {
        return { success: false, message: error.message, data: false };
    }
}

export const addCategory = async (categoryDto: CategoryDTO, apiUrl?: string): Promise<ServiceResponse<CategoryDTO>> => {
    try {
        const response = await axios.post<ServiceResponse<CategoryDTO>>(`${apiUrl}/api/Category/addCategory`, categoryDto);
        return response.data;
    } catch (error: any) {
        return { success: false, message: error.message, data: {} as CategoryDTO };
    }
}

export const updateCategory = async (categoryDto: CategoryDTO, apiUrl?: string): Promise<ServiceResponse<CategoryDTO>> => {
    try {
        const response = await axios.put<ServiceResponse<CategoryDTO>>(`${apiUrl}/Category/updateCategory`, categoryDto);
        return response.data;
    } catch (error: any) {
        return { success: false, message: error.message, data: {} as CategoryDTO };
    }
}

export const addPricingModels = async (packageId: number, pricingModels: PricingModelDTO[], apiUrl?: string): Promise<ServiceResponse<PricingModelDTO[]>> => {
    try {
        const response = await axios.post<ServiceResponse<PricingModelDTO[]>>(`${apiUrl}/Package/addPricingModel/${packageId}`, pricingModels);
        return response.data;
    } catch (error: any) {
        return { success: false, message: error.message, data: [] };
    }
};

export const updatePricingModels = async (pricingModels: PricingModelDTO[], apiUrl?: string): Promise<ServiceResponse<PricingModelDTO[]>> => {
    console.log("Updating Pricing Models:", pricingModels); // Log the data being sent
    try {
        const response = await axios.put<ServiceResponse<PricingModelDTO[]>>(`${apiUrl}/Package/updatePricingModels`, pricingModels);
        return response.data;
    } catch (error: any) {
        return { success: false, message: error.message, data: [] };
    }
};





export const deletePricingModel = async (pricingModelId: number, packageId: number, apiUrl?: string): Promise<ServiceResponse<boolean>> => {
    try {
        const response = await axios.delete<ServiceResponse<boolean>>(`${apiUrl}/Package/deletePricingModel/${packageId}/${pricingModelId}`);
        console.log("Deleting Pricing Model:", pricingModelId, packageId); // Log the data being sent
        return response.data;
    } catch (error: any) {
        return { success: false, message: error.message, data: false };
    }
}

